/**
 * Format seconds to M:SS.t or H:MM:SS.t (tenths precision)
 */
export const formatTime = seconds => {
	const totalSeconds = Number(seconds);
	if (!totalSeconds || Number.isNaN(totalSeconds)) return '0:00.0';

	// Round first, then decompose to avoid displaying xx:60.0
	const totalTenths = Math.round(totalSeconds * 10);
	const hours = Math.floor(totalTenths / 36000);
	const mins = Math.floor((totalTenths % 36000) / 600);
	const padded = ((totalTenths % 600) / 10).toFixed(1).padStart(4, '0');

	if (hours > 0) {
		return `${hours}:${mins.toString().padStart(2, '0')}:${padded}`;
	}

	return `${mins}:${padded}`;
};

/**
 * Format file size in bytes to human-readable format
 */
export const formatFileSize = bytes => {
	if (!bytes || bytes === 0) return '0 B';

	const k = 1024;
	const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));

	return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Format upload speed
 */
export const formatSpeed = bytesPerSecond => {
	return `${formatFileSize(bytesPerSecond)}/s`;
};

/**
 * A drone recorder's MPEG transport stream (.ts, often with flight data). Browsers cannot play it, so it is
 * uploaded without a preview; the results play the converted copy the analysis makes.
 */
export const isTransportStream = file =>
	file?.type === 'video/mp2t' || /\.ts$/i.test(file?.name || '');

/**
 * Validate video file format and size
 */
export const validateVideoFile = async file => {
	try {
		// 1. Type check
		const transportStream = isTransportStream(file);
		if (!transportStream && !['video/mp4', 'video/quicktime'].includes(file.type)) {
			throw new Error('Invalid video format. Only MP4, MOV and TS are supported.');
		}

		// 2. Size check (10GB max)
		const maxSize = 10 * 1024 * 1024 * 1024;
		if (file.size > maxSize) {
			throw new Error('Video file too large. Maximum size is 10GB.');
		}

		// 3. Basic file integrity check
		if (file.size === 0) {
			throw new Error('Video file is empty.');
		}

		// 4. Playback validation
		if (transportStream) return { valid: true };
		const canPlay = await validateVideoPlayback(file);
		if (!canPlay) {
			throw new Error(
				'This video cannot be played. The file may not be supported by your browser or may be corrupted. Try a different browser.'
			);
		}

		return { valid: true };
	} catch (error) {
		return { valid: false, error: error.message };
	}
};

/**
 * Validate that video can be played in browser
 */
export const validateVideoPlayback = file => {
	return new Promise(resolve => {
		const video = document.createElement('video');
		const url = URL.createObjectURL(file);
		let settled = false;

		video.preload = 'metadata';
		video.src = url;

		const cleanup = () => {
			URL.revokeObjectURL(url);
			video.remove();
		};

		const finalize = result => {
			if (settled) return;
			settled = true;
			clearTimeout(timeoutId);
			cleanup();
			resolve(result);
		};

		// Success - video can be loaded
		video.onloadedmetadata = () => {
			// Additional check: ensure video has valid dimensions and duration
			const isValid = video.videoWidth > 0 && video.videoHeight > 0 && video.duration > 0;
			finalize(isValid);
		};

		// Error - video cannot be played
		video.onerror = () => {
			finalize(false);
		};

		// Timeout after 30 seconds
		const timeoutId = setTimeout(() => {
			finalize(false);
		}, 30000);
	});
};

/**
 * Extract metadata from video file
 */
export const extractVideoMetadata = videoElement => {
	return {
		duration: videoElement.duration,
		width: videoElement.videoWidth,
		height: videoElement.videoHeight,
		aspectRatio: videoElement.videoWidth / videoElement.videoHeight,
	};
};

/**
 * Convert display coordinates to native video resolution coordinates
 */
export const displayToNativeCoordinates = (displayX, displayY, videoElement, canvasElement) => {
	const rect = canvasElement.getBoundingClientRect();

	if (!rect.width || !rect.height || !videoElement.videoWidth || !videoElement.videoHeight) {
		return [0, 0];
	}

	const scaleX = videoElement.videoWidth / rect.width;
	const scaleY = videoElement.videoHeight / rect.height;

	return [Math.round(displayX * scaleX), Math.round(displayY * scaleY)];
};

/**
 * Convert native video resolution coordinates to display coordinates
 */
/**
 * A counting line stays this share of the frame's shorter side inside its edges (the backend keeps a line job's line
 * there too, video_tracks.LINE_EDGE_MARGIN): an animal walking in is first found part-way into the picture, so a line
 * nearer the edge is crossed before the animal is seen.
 */
export const LINE_EDGE_MARGIN = 0.03;

/** A line end [x, y] in source pixels, kept inside a width × height frame less its edge band, in whole pixels. */
export const keepLineInside = (p, width, height) => {
	const m = Math.round(LINE_EDGE_MARGIN * Math.min(width, height));
	return [
		Math.max(m, Math.min(width - m, Math.round(p[0]))),
		Math.max(m, Math.min(height - m, Math.round(p[1]))),
	];
};

export const nativeToDisplayCoordinates = (nativeX, nativeY, videoElement, canvasElement) => {
	const rect = canvasElement.getBoundingClientRect();

	if (!rect.width || !rect.height || !videoElement.videoWidth || !videoElement.videoHeight) {
		return [0, 0];
	}

	const scaleX = rect.width / videoElement.videoWidth;
	const scaleY = rect.height / videoElement.videoHeight;

	return [nativeX * scaleX, nativeY * scaleY];
};

/**
 * Calculate estimated processing time based on video duration and configuration
 */
export const calculateEstimatedProcessingTime = (duration, configuration) => {
	// Rough estimate: 1 second of video = 2-5 seconds of processing
	const baseTime = duration * 3;

	// Adjust based on configuration
	let multiplier = 1;

	if (configuration.countingMethod === 'line_based') {
		multiplier *= 0.8; // Slightly faster
	}

	return Math.round(baseTime * multiplier);
};
